import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import moment from "moment";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { BackArrow } from "components/CustomComponents/BackArrow";
import { DownloadForOffline } from "@mui/icons-material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  optionalAvathar,
  defaultAllSelection,
  tableRowSx,
} from "services/constants";
import { setGlobalSearchValue } from "services/Redux/userToken";
import { useDispatch } from "react-redux";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.css";
import "../../../assets/scss/datepicker.css";
import { leaveManagamentPlaceholder } from "services/constants/PlaceHolder";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { TextField, InputAdornment, IconButton } from "@material-ui/core";
import { tooltipStyleProps } from "services/constants/Styles";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { CustomAutoComplete } from "components/CustomComponents";
import { lateReportHeader } from "../TeamZoneHeaders";

const LateCountReport = ({ setShowReport }) => {
  const {
    domain,
    token,
    userDetails,
    globalSearchValue,
    topbarCompanyId,
    topbarCompanyName,
  } = useSelector((state) => state.tokenReducer);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [countRepDetails, setCountRepDetails] = useState([]);
  const [masterDataForSearch, setMasterDataForSearch] = useState([]);
  const [startDate, setStartDate] = useState(
    moment().subtract(6, "days").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}`
  );
  const [departmentName, setDepartmentName] = useState("All");
  const [departmentId, setDepartmentId] = useState(defaultAllSelection);
  const [userName, setUserName] = useState("All");
  const [userId, setUserId] = useState(defaultAllSelection);
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);
  const [recallApi, setRecallApi] = useState(true);
  const [totalLateDuration, setLateTotalDuration] = useState("");
  const [getDepartmentData, setGetDepartmentData] = useState([]);
  const { result: membersData, setLoad: reCallMemberApi } = useFetch(
    `${domain}get-department-based-user/?department_id=${departmentId}&user_id=${userDetails.id}`
  );
  const triggerDate = true;
  const showToday = false;

  useEffect(() => {
    const getFilter = () => {
      const allDepartments = {
        id: defaultAllSelection,
        label: "All",
        department_name: "All",
      };
      setGetDepartmentData([allDepartments, ...departmentData]);
    };
    getFilter();
  }, [departmentData]);

  useEffect(() => {
    const fetchUserCountDetails = async () => {
      try {
        setIsLoading(true);
        let searchStartDate = startDate.split("/").reverse().join("-");
        let searchEndDate = endDate.split("/").reverse().join("-");
        const response = await fetch(
          `${domain}late-report/?department_id=${departmentId}&start_date=${searchStartDate}&end_date=${searchEndDate}&company_id=${topbarCompanyId}&user_id=${userDetails.id}&selected_user_id=${userId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          setCountRepDetails(res);
          setMasterDataForSearch(res);
          setLateTotalDuration(
            res?.length === 0 ? "00:00" : res.at(-1).sum_of_late_hours
          );
        } else if (response.status === 409) {
          setCountRepDetails([]);
          setMasterDataForSearch([]);
          sessionHandling();
        } else {
          setCountRepDetails([]);
          setMasterDataForSearch([]);
          throw new Error(res.error);
        }
      } catch (error) {
        setCountRepDetails([]);
        setMasterDataForSearch([]);
        showToast("error", error.message);
      } finally {
        setIsLoading(false);
      }
    };
    if (startDate && endDate && departmentId && recallApi && userId) {
      fetchUserCountDetails();
      setRecallApi(false);
    }
  }, [
    domain,
    token,
    startDate,
    endDate,
    departmentId,
    userDetails.id,
    topbarCompanyId,
    recallApi,
    userId,
  ]);

  useEffect(() => {
    const filteredResults = masterDataForSearch?.filter((item) =>
      [
        "date",
        "check_out_time",
        "check_in_time",
        "company",
        "department",
        "shift_end_time",
        "shift_max_end_time",
        "shift_start_time",
        "shift_max_start_time",
        "shift_name",
        "actual_duration",
        "username",
      ].some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase())
      )
    );
    setCountRepDetails(filteredResults);
    setPage(0);
  }, [globalSearchValue, masterDataForSearch]);

  const sortedOtData = countRepDetails
    .filter((item) => item.date)
    .sort((a, b) => {
      const [yearA, monthA, dayA] = a.date.split("-");
      const [yearB, monthB, dayB] = b.date.split("-");
      const formattedA = `${yearA}-${monthA}-${dayA}`;
      const formattedB = `${yearB}-${monthB}-${dayB}`;
      return formattedB.localeCompare(formattedA);
    });
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const updateUserName = (event, data) => {
    if (data) {
      setUserName(data);
      setUserId(data.user_id);
      reCallMemberApi(true);
      setRecallApi(true);
      setShowAutoCompleteAvathar(false);
    } else {
      setShowAutoCompleteAvathar(true);
    }
  };

  const updateDepartmentName = (event, data) => {
    if (data) {
      setDepartmentName(data.department_name);
      setDepartmentId(data.id);
      reCallMemberApi(true);
      setRecallApi(true);
      setUserName("All");
      setUserId(defaultAllSelection);
    }
  };

  const getOvertimeReport = () => {
    let dataPreparation = [];
    sortedOtData?.forEach((element) => {
      let overAllData = {
        date: element?.date,
        employeename: element?.username,
        employee_id: element?.employee_id,
        departmentname: element?.department ? element?.department : "N/A",
        shiftname: element?.shift_name ? element?.shift_name : "Unassigned",
        shiftstarttime: element?.shift_start_time
          ? element?.shift_start_time
          : "N/A",
        shiftMaxStartTime: element?.shift_max_start_time
          ? element?.shift_max_start_time
          : "N/A",
        shiftendtime: element?.shift_end_time ? element?.shift_end_time : "N/A",
        shiftMaxEndTime: element?.shift_max_end_time
          ? element?.shift_max_end_time
          : "N/A",
        checkintime: element?.check_in_time ? element?.check_in_time : "N/A",
        checkouttime: element?.check_out_time
          ? element?.check_out_time
          : "00:00",
        actualduration: element?.actual_duration
          ? element?.actual_duration
          : "00:00",
      };
      dataPreparation = [...dataPreparation, overAllData];
    });
    const LateCountReport = ["Late Report"];
    const Space = [""];
    const Dates = ["Date"];
    const companyName = ["Company Name"];
    const Department = ["Department Name"];
    const GeneratedOn = ["Report Generated On"];
    const TotalLateHrs = ["Total Late Arrival Duration"];

    LateCountReport.push("");
    Dates.push(`${startDate} to ${endDate}`);
    companyName.push(topbarCompanyName);
    Department.push(departmentName);
    GeneratedOn.push(moment(new Date()).format("DD/MM/YYYY, hh:mm a"));
    TotalLateHrs.push(totalLateDuration ?? "00:00");
    Space.push("");
    const items = dataPreparation;
    const TableHead = ["Date"];
    TableHead.push("Employee Name");
    TableHead.push("Employee Id");
    TableHead.push("Department Name");
    TableHead.push("Shift Name");
    TableHead.push("Shift Start Time");
    TableHead.push("Shift Max Start Time");
    TableHead.push("Shift End Time");
    TableHead.push("Shift Max End Time");
    TableHead.push("CheckIn Time");
    TableHead.push("CheckOut Time");
    TableHead.push("Late Arrival Duration");
    const replacer = (key, value) => (value === null ? "" : value);
    const header = Object.keys(items[0]);
    let csv = items.map((row) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(",")
    );
    csv.unshift(TableHead.join(","));
    csv.unshift(Space.join(","));
    if (userId !== defaultAllSelection) {
      csv.unshift(TotalLateHrs.join(","));
    }
    csv.unshift(GeneratedOn.join(","));
    csv.unshift(Department.join(","));
    csv.unshift(companyName.join(","));
    csv.unshift(Dates.join(","));
    csv.unshift(LateCountReport.join(","));
    csv = csv.join("\r\n");
    const ConvertData = csv;
    const element = document.createElement("a");
    const file = new Blob([ConvertData], {
      type: "text/plain",
    });
    element.href = URL.createObjectURL(file);
    element.download = "LateCountReport.csv";
    document.body.appendChild(element);
    element.click();
  };

  const [state, setState] = useState(() => {
    if (!triggerDate) {
      const today = moment();
      return { start: today.clone(), end: today.clone() };
    } else {
      return {
        start: moment().subtract(6, "days"),
        end: moment(),
      };
    }
  });
  const [trigger, setTrigger] = useState(true);
  const [labelName, setLabelName] = useState(
    triggerDate ? "Last 7 Days" : "Today"
  );

  useEffect(() => {
    const getPastDayDetails = () => {
      if (triggerDate) {
        const defaultStart = moment().subtract(6, "days");
        const defaultEnd = moment();
        setState({ start: defaultStart, end: defaultEnd });
        setLabelName(triggerDate ? "Last 7 Days" : "Today");
        setStartDate(defaultStart.format("DD/MM/YYYY"));
        setEndDate(defaultEnd.format("DD/MM/YYYY"));
      } else {
        const today = moment();
        setState({ start: today, end: today });
        setLabelName(triggerDate ? "Last 7 Days" : "Today");
        setStartDate(today.format("DD/MM/YYYY"));
        setEndDate(today.format("DD/MM/YYYY"));
      }
    };
    if (trigger) {
      getPastDayDetails();
      setTrigger(false);
    }
  }, [trigger, triggerDate]);

  const { start, end } = state;

  const handleCallback = (start, end, rangeLabel) => {
    setState({ start, end });
    setStartDate(moment(new Date(start)).format("DD/MM/YYYY"));
    setEndDate(moment(new Date(end)).format("DD/MM/YYYY"));
    setLabelName(rangeLabel);
    reCallMemberApi(true);
    setRecallApi(true);
  };
  const label = start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");

  const getRanges = () => ({
    ...(showToday && {
      Today: [moment().toDate(), moment().toDate()],
    }),
    Yesterday: [
      moment().subtract(1, "days").toDate(),
      moment().subtract(1, "days").toDate(),
    ],
    "Last 7 Days": [moment().subtract(6, "days").toDate(), moment()],
    "Last 30 Days": [moment().subtract(29, "days").toDate(), moment().toDate()],
    "This Month": [
      moment().startOf("month").toDate(),
      moment().endOf("month").toDate(),
    ],
    "Last Month": [
      moment().subtract(1, "month").startOf("month").toDate(),
      moment().subtract(1, "month").endOf("month").toDate(),
    ],
  });

  useEffect(() => {
    if (topbarCompanyId) {
      setRecallApi(true);
    }
  }, [topbarCompanyId]);

  return isLoading ? (
    <Loader info="Loading..." />
  ) : (
    <React.Fragment>
      <Mui.Grid container spacing={1} sx={{ mt: 0 }}>
        <Mui.Grid item xs={12} sm={12} md={12} lg={12}>
          <Mui.Stack direction="row">
            <BackArrow
              actionFunction={() => {
                dispatch(setGlobalSearchValue(""));
                setShowReport(false);
              }}
            />
            <Mui.Typography
              sx={{
                fontSize: 20,
                fontWeight: "bold",
                mt: 1.5,
              }}
            >
              Late Report
            </Mui.Typography>
          </Mui.Stack>
        </Mui.Grid>
      </Mui.Grid>

      <Mui.Grid container spacing={1} sx={{ paddingLeft: 2, mt: 0.5 }}>
        <Mui.Grid item xs={12} sm={6} md={3} lg={2}>
          <DateRangePicker
            initialSettings={{
              startDate: start.toDate(),
              endDate: end.toDate(),
              ranges: getRanges(),
              maxDate: showToday
                ? moment().toDate()
                : moment().subtract(1, "days").toDate(),
              applyClass: "custom-apply-btn",
              cancelClass: "custom-cancel-btn",
              locale: {
                format: "DD/MM/YYYY",
              },
            }}
            onCallback={handleCallback}
          >
            <TextField
              id="reportrange"
              label={labelName}
              placeholder={leaveManagamentPlaceholder.dateFormat}
              value={label}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton>
                      <DateRangeIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ shrink: true, required: true }}
              fullWidth
            />
          </DateRangePicker>
        </Mui.Grid>
        <Mui.Grid item xs={12} sm={6} md={3} lg={2}>
          <CustomAutoComplete
            dataList={getDepartmentData?.map((obj) => ({
              ...obj,
              label: obj.department_name,
            }))}
            selectedValue={departmentName}
            updateDataFunction={updateDepartmentName}
            placeHolder="Select department name"
            label="Select Department Name"
          />
        </Mui.Grid>
        <Mui.Grid item xs={12} sm={6} md={3} lg={2}>
          <CustomAutoCompleteWithLogo
            dataList={membersData?.map((obj) => ({
              ...obj,
              label: obj?.user_name,
              image: obj?.image_url,
            }))}
            selectedValue={userName}
            updateDataFunction={updateUserName}
            placeHolder="Select employee name"
            label="Select User Name"
            showAvathar={showAutoCompleteAvathar}
            setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
          />
        </Mui.Grid>
        <Mui.Grid
          item
          lg={
            userName?.label === "All" ||
            userName === "All" ||
            sortedOtData?.length === 0
              ? 5.5
              : 3.5
          }
        ></Mui.Grid>
        {userName?.label !== "All" &&
          userName !== "All" &&
          sortedOtData?.length !== 0 && (
            <Mui.Grid item xs={2} sm={2} md={2} lg={2} sx={{ mt: 1.3 }}>
              <Mui.Typography sx={{ fontWeight: 700 }}>
                Total Late Arrival Duration : {totalLateDuration}
              </Mui.Typography>
            </Mui.Grid>
          )}

        <Mui.Grid item xs={2} sm={2} md={2} lg={0.5}>
          <Mui.IconButton
            onClick={() => {
              getOvertimeReport();
            }}
            disabled={sortedOtData?.length === 0 ? true : false}
          >
            <DownloadForOffline
              size="large"
              sx={{ color: themes.downloadIconColor, fontSize: 40, mt: -1 }}
            />
          </Mui.IconButton>
        </Mui.Grid>
      </Mui.Grid>

      <>
        <Mui.Grid container sx={{ overflow: "hidden" }}>
          <Mui.TableContainer
            sx={{ mt: 2, width: "100%", maxHeight: 660, ml: 2, mr: 2 }}
          >
            <Mui.Table className="border">
              <Mui.TableHead
                sx={{
                  backgroundColor: themes.primary,
                  position: "sticky",
                  top: -1,
                  zIndex: 10,
                }}
              >
                <Mui.TableRow sx={tableRowSx}>
                  {lateReportHeader.map((header) => (
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                        textOverflow: "ellipsis",
                      }}
                    >
                      <Mui.Tooltip
                        title={header.name}
                        arrow
                        slotProps={tooltipStyleProps}
                      >
                        {header.name}
                      </Mui.Tooltip>
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {sortedOtData?.length === 0 && !isLoading ? (
                  <InfoMessage dataLength={masterDataForSearch} />
                ) : (
                  sortedOtData
                    ?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                    ?.map((item, index) => (
                      <Mui.TableRow key={item?.id} sx={tableRowSx}>
                        {lateReportHeader.map((header) => (
                          <Mui.TableCell>
                            {header.name === "Employee Name" ? (
                              <Mui.Stack direction="Row">
                                <Mui.Avatar
                                  sx={{ width: 23, height: 23 }}
                                  alt={item?.username?.charAt(0).toUpperCase()}
                                  src={item?.image_url ?? optionalAvathar}
                                />
                                &nbsp;
                                {item?.username && item.username.length > 10 ? (
                                  <Mui.Tooltip
                                    title={item.username}
                                    arrow
                                    slotProps={tooltipStyleProps}
                                  >
                                    <div
                                      style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: 100,
                                      }}
                                    >
                                      {item.username}
                                    </div>
                                  </Mui.Tooltip>
                                ) : (
                                  item?.username ?? "N/A"
                                )}
                              </Mui.Stack>
                            ) : header.name === "Date" ? (
                              item?.date ? (
                                item.date.split("-").reverse().join("-")
                              ) : (
                                "N/A"
                              )
                            ) : item[header.key] ? (
                              item[header.key]
                            ) : (
                              "N/A"
                            )}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    ))
                )}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
        </Mui.Grid>

        {sortedOtData?.length > 10 && (
          <Mui.TablePagination
            className="custom-pagination"
            component="div"
            rowsPerPageOptions={[25, 50, 75, 100]}
            count={sortedOtData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </>
    </React.Fragment>
  );
};
export default LateCountReport;
